import { useState } from 'react';
import { X, Loader2, ChevronLeft } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import { buildWhatsAppLink, buildServiceRequestMessage } from './servicesData';
import StepProjectDetails from './steps/StepProjectDetails';
import StepDescriptionBudget from './steps/StepDescriptionBudget';
import StepSummary from './steps/StepSummary';
import useWhatsApp from '@/lib/useWhatsApp';
import { useLang } from '@/lib/LanguageContext';

const STEP_LABELS = ['Your Info', 'Project', 'Review'];

export default function ServiceRequestModal({ onClose }) {
  const { waText } = useWhatsApp();
  const { t, language } = useLang();
  const [step, setStep] = useState(1);
  const [data, setData] = useState({ name: '', email: '', phone: '', project_types: [], description: '', budget: '' });
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const canNext =
    step === 1 ? data.name && data.email && data.phone && data.project_types.length > 0 :
    step === 2 ? data.description : true;

  const handleSubmit = async () => {
    if (!agreed) {
      setError(t('Please accept the Terms & Conditions and AI Disclaimer to continue.'));
      return;
    }
    setSubmitting(true);
    setError('');
    // Open the tab synchronously, in direct response to the click, so mobile
    // browsers don't treat the later redirect as a blocked popup.
    const waWindow = window.open('', '_blank');
    try {
      await base44.functions.invoke('submitServiceRequest', data);
      // Fire conversion tracking for Google Ads & Meta Ads — does not affect WhatsApp/admin flow
      if (typeof window !== 'undefined') {
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'conversion', { send_to: 'AW-18399328408' });
        }
        if (typeof window.fbq === 'function') {
          window.fbq('track', 'Lead');
        }
        if (typeof window.ttq === 'object' && typeof window.ttq.track === 'function') {
          window.ttq.track('SubmitForm');
        }
      }
      // Server-side TikTok Events API — best-effort, never blocks the WhatsApp redirect
      base44.functions.invoke('tiktokTrackEvent', {
        event: 'SubmitForm',
        email: data.email,
        phone: data.phone,
        url: window.location.href,
      }).catch(() => {});
      const es = language === 'es';
      const msg = buildServiceRequestMessage({ ...data, project_types: data.project_types.map(t) }, es);
      const waLink = buildWhatsAppLink(waText(msg));
      if (waWindow) {
        waWindow.location.href = waLink;
      } else {
        window.location.href = waLink;
      }
      onClose();
    } catch (e) {
      if (waWindow) waWindow.close();
      setError(t('Something went wrong. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="w-full max-w-2xl rounded-2xl border border-border/50 bg-card p-5 relative max-h-[95vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-4 right-4 text-muted-foreground hover:text-foreground">
          <X className="w-5 h-5" />
        </button>

        <h3 className="font-inter font-bold text-xl text-foreground mb-1">{t('Send Your Project')}</h3>
        <p className="text-sm text-muted-foreground mb-3">{language === 'es' ? `Paso ${step} de 3` : `Step ${step} of 3`}: {t(STEP_LABELS[step - 1])}</p>

        <div className="flex gap-2 mb-4">
          {[1, 2, 3].map((s) => (
            <div key={s} className={`h-1 flex-1 rounded-full ${s <= step ? 'bg-primary' : 'bg-secondary'}`} />
          ))}
        </div>

        {step === 1 && <StepProjectDetails data={data} setData={setData} />}
        {step === 2 && <StepDescriptionBudget data={data} setData={setData} />}
        {step === 3 && (
          <StepSummary data={data} agreed={agreed} setAgreed={setAgreed} onEditStep={setStep} />
        )}

        {error && <p className="text-sm text-destructive font-inter mt-4">{error}</p>}

        <div className="flex items-center gap-3 mt-4">
          {step > 1 && (
            <button
              type="button"
              onClick={() => setStep(step - 1)}
              className="flex items-center gap-1 px-4 py-3 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground transition-all">
              <ChevronLeft className="w-4 h-4" /> {t('Back')}
            </button>
          )}
          {step < 3 ? (
            <button
              type="button"
              disabled={!canNext}
              onClick={() => setStep(step + 1)}
              className="flex-1 py-3 bg-primary text-primary-foreground font-inter font-semibold text-sm tracking-wider uppercase rounded-lg hover:bg-primary/90 transition-all disabled:opacity-50">
              {t('Next')}
            </button>
          ) : (
            <button
              type="button"
              disabled={submitting}
              onClick={handleSubmit}
              className="flex-1 py-3 bg-primary text-primary-foreground font-inter font-semibold text-sm tracking-wider uppercase rounded-lg hover:bg-primary/90 transition-all flex items-center justify-center gap-2 disabled:opacity-60">
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> {t('Sending...')}</> : t('Send via WhatsApp')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}